import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  MenuItem,
  Grid,
} from "@mui/material"
import { useForm, Controller } from "react-hook-form"

export default function ClientCreateModal({ open, onClose, onSave, governorates, fullGovernorates, client }) {
  const { control, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      fullName: client?.fullName || "",
      phoneNumber: client?.phoneNumber || "",
      email: client?.email || "",
      address: client?.address || "",
      state: client?.state || "",
      city: client?.city || "",
      postalCode: client?.postalCode || "",
      notes: client?.notes || "",
    },
  })

  const selectedState = watch("state")
  const [cities, setCities] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (selectedState) {
      const stateItem = fullGovernorates.find((g) => g.gouvernorat === selectedState)
      setCities(stateItem?.villes || [])
      if (!stateItem?.villes.includes(watch("city"))) {
        setValue("city", "")
      }
    } else {
      setCities([])
      setValue("city", "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, fullGovernorates])

  useEffect(() => {
    if (client?.clientId) {
      reset({
        fullName: client.fullName || "",
        phoneNumber: client.phoneNumber || "",
        email: client.email || "",
        address: client.address || "",
        state: client.state || "",
        city: client.city || "",
        postalCode: client.postalCode || "",
        notes: client.notes || "",
      })
    } else {
      reset({
        fullName: "",
        phoneNumber: "",
        email: "",
        address: "",
        state: "",
        city: "",
        postalCode: "",
        notes: "",
      })
      setCities([])
    }
  }, [client, reset, open])

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      await onSave(data, client?.clientId)
    } catch (err) {
      // console.error("Error saving client:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{client?.clientId ? "Modifier le client" : "Créer un nouveau client"}</DialogTitle>
      <DialogContent>
        <form id="client-create-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="fullName"
            control={control}
            rules={{ required: "Le nom complet est requis" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Nom complet"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name="phoneNumber"
            control={control}
            rules={{ required: "Le numéro est requis" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Téléphone"
                error={!!error}
                helperText={error?.message}
                inputProps={{ maxLength: 8 }}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Email"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="state"
                control={control}
                rules={{ required: "L'état est requis" }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="État"
                    error={!!error}
                    helperText={error?.message}
                    margin="normal"
                    InputProps={{ sx: { height: 45 } }}
                  >
                    <MenuItem value="" disabled>
                      Sélectionnez un état
                    </MenuItem>

                    {governorates.map((g) => (
                      <MenuItem key={g} value={g}>
                        {g}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="city"
                control={control}
                rules={{ required: "La ville est obligatoire" }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Ville"
                    error={!!error}
                    helperText={error?.message}
                    margin="normal"
                    InputProps={{ sx: { height: 45 } }}
                  >
                    <MenuItem value="" disabled>
                      Sélectionnez une ville
                    </MenuItem>

                    {cities.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          <Controller
            name="address"
            control={control}
            rules={{ required: "L'adresse est requise" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Adresse"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name="postalCode"
            control={control}
            rules={{ required: "Le code postal est requis" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Code postal"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name="notes"
            control={control}
            render={({ field }) => <TextField {...field} fullWidth margin="normal" label="Notes" multiline rows={3} />}
          />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isLoading}>
          Annuler
        </Button>
        <Button
          type="submit"
          form="client-create-form"
          variant="contained"
          color="primary"
          disabled={isLoading}
          sx={{ color: "#fff" }}
        >
          {isLoading ? <CircularProgress size={24} /> : client?.clientId ? "Enregistrer" : "Créer"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
